import { Check, Loader2 } from "lucide-react";
import { OAuthReturnTarget, oauthReturnPath, parseOAuthReturnTarget } from "../lib/oauthReturn";
import { useEffect, useMemo, useState } from "react";
import { FaLinkedin } from "react-icons/fa6";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";

interface OrgCandidate {
  id: string;
  name: string;
  logo?: string;
}

// Non-sensitive payload — no tokens. The personal connection (token holder) is
// already saved server-side in the OAuth callback; /complete reads the token
// from it to add the selected orgs.
interface SelectorPayload {
  userId: string;
  returnTarget: OAuthReturnTarget;
  personal: {
    name: string;
    profile_picture_url?: string;
  };
  orgs: OrgCandidate[];
}

const PERSONAL_KEY = "__personal__";


const LinkedInPageSelector = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [payload, setPayload] = useState<SelectorPayload | null>(null);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set([PERSONAL_KEY]),
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  // Leaving the picker returns to where the connection started (setup,
  // analytics, or Settings), even when the rest of the payload is unusable.
  const [returnTarget, setReturnTarget] = useState<OAuthReturnTarget>({});
  const leave = () => navigate(oauthReturnPath(returnTarget));
  const leaveLabel = returnTarget.returnPath === "/onboarding"
    ? "Back to setup"
    : returnTarget.returnPath === "/analytics"
      ? "Back to analytics"
      : returnTarget.returnPath === "/create-post"
        ? "Back to your post"
        : "Back to settings";

  useEffect(() => {
    const dataParam = searchParams.get("data");
    if (!dataParam) {
      setParseError("No connection data provided.");
      return;
    }
    try {
      const decoded = decodeURIComponent(dataParam);
      const parsed = JSON.parse(decoded);
      setReturnTarget(parseOAuthReturnTarget(parsed));
      if (
        !parsed?.userId ||
        !parsed?.personal?.name ||
        !Array.isArray(parsed?.orgs)
      ) {
        console.error("LinkedIn selector: malformed payload", parsed);
        setParseError(
          "Connection data is out of date. Please start the LinkedIn connection again.",
        );
        return;
      }
      setPayload({
        ...(parsed as SelectorPayload),
        returnTarget: parseOAuthReturnTarget(parsed),
      });
    } catch (err) {
      console.error("Failed to parse connection data:", err);
      setParseError(
        "Couldn't read connection data. Please try connecting again.",
      );
    }
  }, [searchParams]);

  const orgs = useMemo(() => payload?.orgs ?? [], [payload]);
  const personalName = payload?.personal.name ?? "Your LinkedIn";

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleConnect = async () => {
    if (!payload || selected.size === 0) return;

    setIsConnecting(true);
    setError(null);

    const includePersonal = selected.has(PERSONAL_KEY);
    const selectedOrgs = orgs
      .filter((o) => selected.has(o.id))
      .map((o) => ({
        id: o.id,
        name: o.name,
        profile_picture_url: o.logo,
      }));

    try {
      // The endpoint authenticates the caller and acts on req.user.uid, so we
      // send the Firebase bearer token (and no userId in the body).
      const token = await currentUser?.getIdToken();
      if (!token) {
        setError("Your session expired. Please sign in again.");
        setIsConnecting(false);
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/connections/linkedin/complete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            includePersonal,
            selectedOrgs,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || "Failed to connect LinkedIn accounts",
        );
      }

      navigate(oauthReturnPath(payload.returnTarget, "success", "linkedin"));
    } catch (err) {
      console.error("Error connecting LinkedIn accounts:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to connect LinkedIn accounts",
      );
    } finally {
      setIsConnecting(false);
    }
  };

  if (parseError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4">
          <Alert variant="destructive">
            <AlertTitle>LinkedIn connection error</AlertTitle>
            <AlertDescription>{parseError}</AlertDescription>
          </Alert>
          <Button
            onClick={leave}
            variant="outline"
            className="w-full"
          >
            {leaveLabel}
          </Button>
        </div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading LinkedIn accounts…
        </div>
      </div>
    );
  }

  const totalCandidates = 1 + orgs.length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Hero */}
        <header className="mb-10 text-center">
          <div className="inline-flex items-center justify-center gap-2 mb-3 text-muted-foreground">
            <FaLinkedin className="h-5 w-5" />
            <span className="text-xs font-medium uppercase tracking-wider">
              LinkedIn
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Which accounts should we connect?
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            Pick the ones you want to publish to. You can change this later.
          </p>
        </header>

        {error && (
          <Alert variant="destructive" className="mb-8">
            <AlertTitle>Something didn't go through</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Accounts
          </p>
          <span className="text-xs text-muted-foreground tabular-nums">
            {selected.size}/{totalCandidates}
          </span>
        </div>

        <div className="border-t border-border">
          <SelectableRow
            avatar={payload.personal.profile_picture_url}
            fallbackInitial={personalName?.[0]?.toUpperCase()}
            name={personalName}
            kind="Personal profile"
            selected={selected.has(PERSONAL_KEY)}
            onToggle={() => toggle(PERSONAL_KEY)}
          />
          {orgs.map((org) => (
            <SelectableRow
              key={org.id}
              avatar={org.logo}
              fallbackInitial={org.name?.[0]?.toUpperCase()}
              name={org.name}
              kind="Organization"
              selected={selected.has(org.id)}
              onToggle={() => toggle(org.id)}
            />
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 mt-8">
          <Button
            variant="outline"
            onClick={leave}
            disabled={isConnecting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={selected.size === 0 || isConnecting}
          >
            {isConnecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Connecting…
              </>
            ) : (
              `Connect ${selected.size} account${selected.size === 1 ? "" : "s"}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

function SelectableRow({
  avatar,
  fallbackInitial,
  name,
  kind,
  selected,
  onToggle,
}: {
  avatar?: string;
  fallbackInitial?: string;
  name: string;
  kind: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={cn(
        "group w-full flex items-center gap-3 border-t border-border first:border-t-0 py-4 text-left transition-colors",
        "hover:bg-muted/30 focus-visible:outline-none focus-visible:bg-muted/40",
      )}
    >
      {avatar ? (
        <img
          src={avatar}
          alt=""
          className="h-9 w-9 rounded-full object-cover ring-1 ring-border shrink-0"
        />
      ) : (
        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground shrink-0">
          {fallbackInitial ?? "?"}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{name}</p>
        <p className="text-xs text-muted-foreground">{kind}</p>
      </div>
      <span
        className={cn(
          "h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors",
          selected
            ? "bg-foreground border-foreground text-background"
            : "border-border group-hover:border-foreground/30",
        )}
        aria-hidden
      >
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>
    </button>
  );
}

export default LinkedInPageSelector;
